import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type RefObject } from 'react';
import type { AcessoEstado } from '../../api';
import { t } from '../../i18n/idioma';
import { useIdioma } from '../../i18n/useIdioma';
import { NOME_DO_JOGO } from '../../marca';
import { Check, Marca, Seta } from './icones';
import { fundoCss } from './imagens';
import { redesDe } from './links';
import { Redes, Rodape, Secao, SecoesDoJogo, useLarguraDosFundos, usePrecarregarHeroi, useRolou, useTituloDaAba } from './comum';
import { LoginCard, type AbaLogin } from './LoginCard';
import './landing.css';

const COMECAR = [1, 2, 3, 4] as const;
const PERGUNTAS = [1, 2, 3, 4, 5, 6] as const;

/** `/#criar` abre o cartão na aba Criar conta; `/#login` (ou nada), em Entrar. */
function abaDoEndereco(): AbaLogin {
  return window.location.hash === '#criar' ? 'criar' : 'entrar';
}

/** A barra do pé (só no celular) some enquanto o cartão de login está na tela. */
function useCartaoNaTela(raiz: RefObject<HTMLDivElement | null>): boolean {
  const [naTela, setNaTela] = useState(false);
  useEffect(() => {
    const cartao = document.getElementById('login');
    if (!cartao || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(([e]) => setNaTela(e.isIntersecting), { root: raiz.current, threshold: 0.25 });
    obs.observe(cartao);
    return () => obs.disconnect();
  }, [raiz]);
  return naTela;
}

/**
 * A página inicial com o modo restrito desligado (docs/techspec/techspec-landing-aberta.md): a apresentação do jogo
 * da landing da fase, sem pré-registro e sem Founder Packs, com o cartão de login no lugar do pré-registro.
 */
export function LandingAberta({ estado, emailInicial = '', confirmar, senhaLink }: {
  estado: AcessoEstado;
  /** O link do e-mail do acesso manual (`/#e=…`): o cartão abre em Entrar com o e-mail preenchido. */
  emailInicial?: string;
  /** O botão do e-mail de confirmação (`/#confirmar=1&e=…&c=…`): o cartão abre no passo do código. */
  confirmar?: { readonly email: string; readonly codigo: string };
  /** O botão do e-mail de "esqueci a senha" (`/#senha=1&e=…&c=…`): o cartão abre na senha nova. */
  senhaLink?: { readonly email: string; readonly codigo: string };
}) {
  const idioma = useIdioma();
  const raiz = useRef<HTMLDivElement>(null);
  const rolou = useRolou(raiz);
  const largura = useLarguraDosFundos();
  const redes = redesDe(estado.redes);
  const [aba, setAba] = useState<AbaLogin>(abaDoEndereco);
  const cartaoNaTela = useCartaoNaTela(raiz);
  usePrecarregarHeroi(largura);
  useTituloDaAba(t('landing.aberta.tituloPagina'));

  // /#login, /#criar e os links de e-mail: abre no cartão.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash !== '#login' && hash !== '#criar' && !emailInicial && !confirmar && !senhaLink) return;
    document.getElementById('login')?.scrollIntoView({ block: 'center' });
    document.getElementById(`email-${abaDoEndereco()}`)?.focus({ preventScroll: true });
  }, []);

  /** Todos os botões de entrar e criar: rolam até o cartão, trocam a aba e põem o cursor no e-mail. */
  const irLogin = (nova: AbaLogin) => (e: MouseEvent) => {
    e.preventDefault();
    setAba(nova);
    document.getElementById('login')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    window.setTimeout(() => document.getElementById(`email-${nova}`)?.focus({ preventScroll: true }), 450);
  };

  const fundos = {
    '--img-heroi': fundoCss('heroi', largura),
    '--img-final': fundoCss('final', largura)
  } as CSSProperties;

  return (
    <div className="lp lp--aberta" ref={raiz} style={fundos} lang={idioma}>
      <header className={rolou ? 'lp-topo is-rolou' : 'lp-topo'}>
        <div className="lp-envolve lp-topo__in">
          <a className="lp-marca" href="#inicio" aria-label={t('landing.inicio')}>
            <Marca vivo />
            <span>{NOME_DO_JOGO}</span>
          </a>
          <nav aria-label={t('landing.secoes')}>
            <a href="#jogo">{t('landing.nav.jogo')}</a>
            <a href="#telas">{t('landing.nav.telas')}</a>
            <a href="#comecar">{t('landing.aberta.nav.comecar')}</a>
            <a href="#perguntas">{t('landing.nav.perguntas')}</a>
            <a href="/wiki">{t('landing.nav.wiki')}</a>
          </nav>
          <div className="lp-topo__acoes">
            <a className="lp-btn" href="#login" onClick={irLogin('entrar')}>{t('landing.entrar')}</a>
            <a className="lp-btn lp-btn--ouro" href="#criar" onClick={irLogin('criar')}>{t('landing.aberta.criarConta')}</a>
          </div>
        </div>
      </header>

      <main id="inicio">
        <section className="lp-heroi" aria-labelledby="lp-titulo">
          <div className="lp-heroi__fundo" role="img" aria-label={t('landing.heroi.fundo')} />
          <div className="lp-envolve lp-heroi__in">
            <div>
              <span className="lp-selo-fase lp-selo-fase--aberto"><i />{t('landing.aberta.selo')}</span>
              <h1 id="lp-titulo">{t('landing.heroi.titulo')} <em>{t('landing.heroi.tituloOuro')}</em></h1>
              <p className="lp-heroi__lead">{t('landing.heroi.lead')}</p>
              <ul className="lp-heroi__pontos">
                <li><Check />{t('landing.heroi.ponto1')}</li>
                <li><Check />{t('landing.heroi.ponto2')}</li>
                <li><Check />{t('landing.heroi.ponto3')}</li>
              </ul>
            </div>
            <div className="lp-gump lp-gump--login" id="login">
              <LoginCard aba={aba} onAba={setAba} emailInicial={emailInicial} confirmar={confirmar} senhaLink={senhaLink} />
            </div>
          </div>
        </section>

        <SecoesDoJogo />

        <Secao id="comecar" pedra centro tituloId="lp-t-comecar" sobre={t('landing.aberta.comecar.sobre')}
          titulo={t('landing.aberta.comecar.titulo')} texto={t('landing.aberta.comecar.texto')}>
          <ol className="lp-trilha lp-trilha--comecar">
            {COMECAR.map(n => (
              <li key={n}>
                <div className="lp-trilha__n">{n}</div>
                <h3>{t(`landing.aberta.c${n}.titulo`)}</h3>
                <p>{t(`landing.aberta.c${n}.texto`)}</p>
              </li>
            ))}
          </ol>
          <div className="lp-comecar__acoes">
            <a className="lp-btn lp-btn--ouro" href="#criar" onClick={irLogin('criar')}>
              {t('landing.aberta.comecar.criar')} <Seta />
            </a>
            <a className="lp-btn" href="#login" onClick={irLogin('entrar')}>{t('landing.aberta.comecar.jaTenho')}</a>
          </div>
        </Secao>

        <Secao id="perguntas" centro tituloId="lp-t-perguntas" sobre={t('landing.nav.perguntas')} titulo={t('landing.perguntas.titulo')}>
          <div className="lp-perguntas">
            {PERGUNTAS.map(n => (
              <details key={n}>
                <summary>{t(`landing.aberta.q${n}.p`)}</summary>
                <p>{t(`landing.aberta.q${n}.r`)}</p>
              </details>
            ))}
          </div>
        </Secao>

        <section className="lp-final" aria-labelledby="lp-t-final">
          <div className="lp-envolve">
            <span className="lp-sobre">{t('landing.final.sobre')}</span>
            <h2 id="lp-t-final">{t('landing.aberta.final.titulo')}</h2>
            <p>{t('landing.aberta.final.texto')}</p>
            <Redes redes={redes} />
            <div className="lp-final__acoes">
              <a className="lp-btn lp-btn--ouro" href="#criar" onClick={irLogin('criar')}>
                {t('landing.aberta.final.criar')} <Seta />
              </a>
              <a className="lp-btn" href="#login" onClick={irLogin('entrar')}>{t('landing.entrar')}</a>
            </div>
          </div>
        </section>
      </main>

      <Rodape idioma={idioma} redes={redes}>
        <a href="#login" onClick={irLogin('entrar')}>{t('landing.entrar')}</a>
        <a href="#criar" onClick={irLogin('criar')}>{t('landing.aberta.criarConta')}</a>
      </Rodape>

      <div className={cartaoNaTela ? 'lp-barra-jogar is-escondida' : 'lp-barra-jogar'} aria-hidden={cartaoNaTela || undefined}>
        <a className="lp-btn" href="#login" tabIndex={cartaoNaTela ? -1 : undefined} onClick={irLogin('entrar')}>{t('landing.entrar')}</a>
        <a className="lp-btn lp-btn--ouro" href="#criar" tabIndex={cartaoNaTela ? -1 : undefined} onClick={irLogin('criar')}>
          {t('landing.aberta.criarConta')}
        </a>
      </div>
    </div>
  );
}
